import { apiFetch } from 'app/javascript/services/api';

export type BeltRankValue =
  | 'white'
  | 'gray'
  | 'gray_white'
  | 'gray_black'
  | 'yellow'
  | 'yellow_white'
  | 'yellow_black'
  | 'orange'
  | 'orange_white'
  | 'orange_black'
  | 'green'
  | 'green_white'
  | 'green_black'
  | 'blue'
  | 'purple'
  | 'brown'
  | 'black'
  | 'red_black'
  | 'red_white'
  | 'red';

// Graduation hierarchy; persisted Rails enum values remain unchanged.
export const BELT_RANK_ORDER: Record<BeltRankValue, number> = {
  white: 0,
  // Kids ranks
  gray_white: 1,
  gray: 2,
  gray_black: 3,
  yellow_white: 4,
  yellow: 5,
  yellow_black: 6,
  orange_white: 7,
  orange: 8,
  orange_black: 9,
  green_white: 10,
  green: 11,
  green_black: 12,
  // Adult ranks
  blue: 20,
  purple: 30,
  brown: 40,
  black: 50,
  red_black: 60,
  red_white: 70,
  red: 80,
};

export const KIDS_RANKS: BeltRankValue[] = [
  'gray_white',
  'gray',
  'gray_black',
  'yellow_white',
  'yellow',
  'yellow_black',
  'orange_white',
  'orange',
  'orange_black',
  'green_white',
  'green',
  'green_black',
];

export const ADULT_RANKS: BeltRankValue[] = ['blue', 'purple', 'brown', 'black', 'red_black', 'red_white', 'red'];

export interface Grade {
  id: number;
  number: number;
  received_at: string;
  received_by_id: number | null;
  received_by_name: string | null;
  confirmed: boolean;
  created_at: string;
  updated_at: string;
}

export interface AvailableRanksResponse {
  age: number | null;
  available_ranks: BeltRankValue[];
  existing_ranks: BeltRankValue[];
}

export interface Belt {
  id: number;
  rank: BeltRankValue;
  belt_received_at: string | null;
  belt_received_by_id: number | null;
  belt_received_by_name: string | null;
  confirmed: boolean;
  grades: Grade[];
  check_in_photos?: CheckInPhoto[];
  created_at: string;
  updated_at: string;
}

export interface CheckInPhoto {
  id: number;
  class_date: string;
  photo_url: string;
  thumbnail_url: string;
}

export interface CreateBeltParams {
  rank: BeltRankValue;
  belt_received_at?: string | null;
  belt_received_by_id?: number;
}

export interface UpdateBeltParams {
  rank?: BeltRankValue;
  belt_received_at?: string | null;
  belt_received_by_id?: number;
}

export interface CreateGradeParams {
  number: number;
  received_at: string;
  received_by_id?: number;
}

export interface UpdateGradeParams {
  received_at?: string;
  received_by_id?: number;
}

const BELTS_PATH = '/api/v1/bjj/belts';

// The belt endpoints 404 when the user never opted into a BJJ profile
// (created only via POST /api/v1/bjj/profile or the academy flows). Callers
// use this error to show the opt-in CTA instead of a generic failure.
export class BjjProfileMissingError extends Error {
  constructor() {
    super('Perfil BJJ ainda não criado');
    this.name = 'BjjProfileMissingError';
  }
}

export const beltsService = {
  async list(): Promise<Belt[]> {
    const response = await apiFetch(BELTS_PATH);

    if (response.status === 404) throw new BjjProfileMissingError();
    if (!response.ok) {
      throw new Error('Failed to fetch belts');
    }

    return response.json();
  },

  async getAvailableRanks(): Promise<AvailableRanksResponse> {
    const response = await apiFetch(`${BELTS_PATH}/available_ranks`);

    if (response.status === 404) throw new BjjProfileMissingError();
    if (!response.ok) {
      throw new Error('Failed to fetch available ranks');
    }

    return response.json();
  },

  async get(id: number): Promise<Belt> {
    const response = await apiFetch(`${BELTS_PATH}/${id}`);

    if (!response.ok) {
      throw new Error('Failed to fetch belt');
    }

    return response.json();
  },

  async create(params: CreateBeltParams): Promise<Belt> {
    const response = await apiFetch(BELTS_PATH, {
      method: 'POST',
      body: JSON.stringify({ belt: params }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.errors?.join(', ') || 'Failed to create belt');
    }

    return response.json();
  },

  async update(id: number, params: UpdateBeltParams): Promise<Belt> {
    const response = await apiFetch(`${BELTS_PATH}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ belt: params }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.errors?.join(', ') || 'Failed to update belt');
    }

    return response.json();
  },

  async delete(id: number): Promise<void> {
    const response = await apiFetch(`${BELTS_PATH}/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error('Failed to delete belt');
    }
  },

  // Grade operations
  async listGrades(beltId: number): Promise<Grade[]> {
    const response = await apiFetch(`${BELTS_PATH}/${beltId}/grades`);

    if (!response.ok) {
      throw new Error('Failed to fetch grades');
    }

    return response.json();
  },

  async createGrade(beltId: number, params: CreateGradeParams): Promise<Grade> {
    const response = await apiFetch(`${BELTS_PATH}/${beltId}/grades`, {
      method: 'POST',
      body: JSON.stringify({ grade: params }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.errors?.join(', ') || 'Failed to create grade');
    }

    return response.json();
  },

  async updateGrade(beltId: number, gradeId: number, params: UpdateGradeParams): Promise<Grade> {
    const response = await apiFetch(`${BELTS_PATH}/${beltId}/grades/${gradeId}`, {
      method: 'PATCH',
      body: JSON.stringify({ grade: params }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.errors?.join(', ') || 'Failed to update grade');
    }

    return response.json();
  },

  async deleteGrade(beltId: number, gradeId: number): Promise<void> {
    const response = await apiFetch(`${BELTS_PATH}/${beltId}/grades/${gradeId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error('Failed to delete grade');
    }
  },
};

export const BELT_LABELS: Record<BeltRankValue, string> = {
  white: 'Branca',
  gray: 'Cinza',
  gray_white: 'Cinza e Branca',
  gray_black: 'Cinza e Preta',
  yellow: 'Amarela',
  yellow_white: 'Amarela e Branca',
  yellow_black: 'Amarela e Preta',
  orange: 'Laranja',
  orange_white: 'Laranja e Branca',
  orange_black: 'Laranja e Preta',
  green: 'Verde',
  green_white: 'Verde e Branca',
  green_black: 'Verde e Preta',
  blue: 'Azul',
  purple: 'Roxa',
  brown: 'Marrom',
  black: 'Preta',
  red_black: 'Vermelha-Preta',
  red_white: 'Vermelha-Branca',
  red: 'Vermelha',
};

export const ADULT_BELT_OPTIONS: { value: BeltRankValue; label: string }[] = [
  { value: 'white', label: 'Branca' },
  { value: 'blue', label: 'Azul' },
  { value: 'purple', label: 'Roxa' },
  { value: 'brown', label: 'Marrom' },
  { value: 'black', label: 'Preta' },
  { value: 'red_black', label: 'Vermelha-Preta' },
  { value: 'red_white', label: 'Vermelha-Branca' },
  { value: 'red', label: 'Vermelha' },
];

export const KIDS_BELT_OPTIONS: { value: BeltRankValue; label: string }[] = [
  { value: 'white', label: 'Branca' },
  { value: 'gray_white', label: 'Cinza e Branca' },
  { value: 'gray', label: 'Cinza' },
  { value: 'gray_black', label: 'Cinza e Preta' },
  { value: 'yellow_white', label: 'Amarela e Branca' },
  { value: 'yellow', label: 'Amarela' },
  { value: 'yellow_black', label: 'Amarela e Preta' },
  { value: 'orange_white', label: 'Laranja e Branca' },
  { value: 'orange', label: 'Laranja' },
  { value: 'orange_black', label: 'Laranja e Preta' },
  { value: 'green_white', label: 'Verde e Branca' },
  { value: 'green', label: 'Verde' },
  { value: 'green_black', label: 'Verde e Preta' },
];

// Maps Portuguese labels back to BeltRankValue (for legacy support)
const LABEL_TO_RANK: Record<string, BeltRankValue> = {
  branca: 'white',
  cinza: 'gray',
  'cinza e branca': 'gray_white',
  'cinza e preta': 'gray_black',
  amarela: 'yellow',
  'amarela e branca': 'yellow_white',
  'amarela e preta': 'yellow_black',
  laranja: 'orange',
  'laranja e branca': 'orange_white',
  'laranja e preta': 'orange_black',
  verde: 'green',
  'verde e branca': 'green_white',
  'verde e preta': 'green_black',
  azul: 'blue',
  roxa: 'purple',
  marrom: 'brown',
  preta: 'black',
  'vermelha-preta': 'red_black',
  'vermelha e preta': 'red_black',
  'vermelha-branca': 'red_white',
  'vermelha e branca': 'red_white',
  vermelha: 'red',
};

/**
 * Type guard — true when the input is a known BeltRankValue (lowercase English).
 * Use this before casting raw API strings into the union.
 */
export function isBeltRank(value: unknown): value is BeltRankValue {
  return typeof value === 'string' && value in BELT_RANK_ORDER;
}

/**
 * Normalizes any belt rank string to BeltRankValue
 * Handles: lowercase English ('white'), Portuguese ('Branca', 'branca'), etc.
 */
export function normalizeBeltRank(rank: string): BeltRankValue {
  const lower = rank.toLowerCase();

  // Already a valid BeltRankValue
  if (lower in BELT_RANK_ORDER) {
    return lower as BeltRankValue;
  }

  // Portuguese label
  if (lower in LABEL_TO_RANK) {
    return LABEL_TO_RANK[lower];
  }

  return 'white';
}

/**
 * Gets the translated label for a belt rank
 */
export function getBeltLabel(rank: BeltRankValue | string): string {
  const normalized = normalizeBeltRank(rank);
  return BELT_LABELS[normalized];
}

/**
 * Gets the sort order for a belt rank (higher = more graduated)
 */
export function getBeltSortOrder(rank: BeltRankValue | string): number {
  const normalized = normalizeBeltRank(rank);
  return BELT_RANK_ORDER[normalized];
}

/**
 * Checks if a rank is a kids rank
 */
export function isKidsRank(rank: BeltRankValue | string): boolean {
  const normalized = normalizeBeltRank(rank);
  return KIDS_RANKS.includes(normalized);
}

/**
 * Checks if a rank is an adult rank (includes white which is shared)
 */
export function isAdultRank(rank: BeltRankValue | string): boolean {
  const normalized = normalizeBeltRank(rank);
  return ADULT_RANKS.includes(normalized) || normalized === 'white';
}

// Belt color themes for UI components
export interface BeltTheme {
  hex: string;
  borderHex: string;
  gradient: string;
  glow: string;
  glowLarge: string;
  accent: string;
  bg: string;
  isLight: boolean;
}

type BaseBeltColor = 'white' | 'gray' | 'yellow' | 'orange' | 'green' | 'blue' | 'purple' | 'brown' | 'black' | 'red';

const BELT_THEMES: Record<BaseBeltColor, BeltTheme> = {
  red: {
    hex: '#dc2626', borderHex: '#b91c1c', gradient: 'from-red-500 to-red-700',
    glow: 'shadow-red-500/20', glowLarge: 'shadow-[0_0_60px_rgba(220,38,38,0.5)]',
    accent: 'from-red-400 to-red-600', bg: 'bg-red-500/30', isLight: false,
  },
  white: {
    hex: '#f1f5f9',
    borderHex: '#cbd5e1',
    gradient: 'from-slate-100 to-slate-300',
    glow: 'shadow-white/10',
    glowLarge: 'shadow-[0_0_60px_rgba(248,250,252,0.5)]',
    accent: 'from-slate-100 to-slate-300',
    bg: 'bg-slate-200/30',
    isLight: true,
  },
  gray: {
    hex: '#9ca3af',
    borderHex: '#9ca3af',
    gradient: 'from-gray-400 to-gray-600',
    glow: 'shadow-gray-500/20',
    glowLarge: 'shadow-[0_0_60px_rgba(156,163,175,0.5)]',
    accent: 'from-gray-400 to-gray-600',
    bg: 'bg-gray-500/30',
    isLight: false,
  },
  yellow: {
    hex: '#eab308',
    borderHex: '#eab308',
    gradient: 'from-yellow-400 to-yellow-600',
    glow: 'shadow-yellow-500/20',
    glowLarge: 'shadow-[0_0_60px_rgba(234,179,8,0.5)]',
    accent: 'from-yellow-400 to-yellow-600',
    bg: 'bg-yellow-500/30',
    isLight: false,
  },
  orange: {
    hex: '#f97316',
    borderHex: '#f97316',
    gradient: 'from-orange-400 to-orange-600',
    glow: 'shadow-orange-500/20',
    glowLarge: 'shadow-[0_0_60px_rgba(249,115,22,0.5)]',
    accent: 'from-orange-400 to-orange-600',
    bg: 'bg-orange-500/30',
    isLight: false,
  },
  green: {
    hex: '#22c55e',
    borderHex: '#22c55e',
    gradient: 'from-green-400 to-green-600',
    glow: 'shadow-green-500/20',
    glowLarge: 'shadow-[0_0_60px_rgba(34,197,94,0.5)]',
    accent: 'from-green-400 to-green-600',
    bg: 'bg-green-500/30',
    isLight: false,
  },
  blue: {
    hex: '#2563eb',
    borderHex: '#1976D2',
    gradient: 'from-blue-500 to-blue-700',
    glow: 'shadow-blue-500/20',
    glowLarge: 'shadow-[0_0_60px_rgba(37,99,235,0.6)]',
    accent: 'from-blue-400 to-blue-600',
    bg: 'bg-blue-500/30',
    isLight: false,
  },
  purple: {
    hex: '#7c3aed',
    borderHex: '#7B1FA2',
    gradient: 'from-purple-600 to-purple-800',
    glow: 'shadow-purple-500/20',
    glowLarge: 'shadow-[0_0_60px_rgba(124,58,237,0.6)]',
    accent: 'from-purple-400 to-purple-600',
    bg: 'bg-purple-500/30',
    isLight: false,
  },
  brown: {
    hex: '#78350f',
    borderHex: '#5D4037',
    gradient: 'from-amber-700 to-amber-900',
    glow: 'shadow-amber-500/20',
    glowLarge: 'shadow-[0_0_60px_rgba(120,53,15,0.6)]',
    accent: 'from-amber-600 to-amber-800',
    bg: 'bg-amber-700/30',
    isLight: false,
  },
  black: {
    hex: '#18181b',
    borderHex: '#dc2626',
    gradient: 'from-zinc-800 to-zinc-900',
    glow: 'shadow-yellow-500/20',
    glowLarge: 'shadow-[0_0_60px_rgba(234,179,8,0.5)]',
    accent: 'from-yellow-400 to-amber-500',
    bg: 'bg-yellow-500/20',
    isLight: false,
  },
};

/**
 * Maps a belt rank to its base color for theming
 */
function getBaseColor(rank: BeltRankValue): BaseBeltColor {
  if (rank.startsWith('gray')) return 'gray';
  if (rank.startsWith('yellow')) return 'yellow';
  if (rank.startsWith('orange')) return 'orange';
  if (rank.startsWith('green')) return 'green';
  if (rank.startsWith('red')) return 'red';
  return rank as BaseBeltColor;
}

/**
 * Gets the color theme for a belt rank
 */
export function getBeltTheme(rank: BeltRankValue | string): BeltTheme {
  const normalized = normalizeBeltRank(rank);
  const baseColor = getBaseColor(normalized);
  return BELT_THEMES[baseColor];
}

export function getMaxGrades(rank: BeltRankValue | string): number {
  const normalized = normalizeBeltRank(rank);
  if (normalized.startsWith('red')) return 0;
  return normalized === 'black' ? 6 : 4;
}
